import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, ArrowUpRight, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export default function Billing() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/subscription/current");
      setSub(data);
    } catch (e) {
      toast.error("Errore nel caricamento abbonamento");
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/billing",
      });
      window.location.href = data.portal_url;
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Errore apertura portale");
    }
  };

  if (!sub) return <div className="p-8 text-center text-[#8C8B8A]">Caricamento…</div>;

  const isActive = sub.status === "active" || sub.status === "trialing";
  const isTrialing = sub.status === "trialing";
  const quota = sub.email_quota_monthly || 0;
  const used = sub.email_used_this_cycle || 0;
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const barColor = pct > 90 ? "#C85A32" : pct > 70 ? "#D4A017" : "#2C4C3B";

  const statusLabel = {
    trialing: "In prova",
    active: "Attivo",
    past_due: "In sospeso",
    canceled: "Annullato",
    incomplete: "Incompleto",
    none: "Nessun piano",
  }[sub.status] || sub.status;

  const statusColor = {
    trialing: "#C85A32",
    active: "#2C4C3B",
    past_due: "#991B1B",
    canceled: "#8C8B8A",
    none: "#8C8B8A",
  }[sub.status] || "#8C8B8A";

  return (
    <div className="space-y-8">
      <div>
        <div className="label-mono">Abbonamento</div>
        <h1 className="font-display text-4xl mt-2 font-light">Fatturazione &amp; piano</h1>
        <p className="text-sm text-[#5C5B5A] mt-2 max-w-xl">
          Gestisci il tuo piano, scarica le fatture e modifica il metodo di pagamento.
        </p>
      </div>

      {/* Current plan card */}
      <div data-testid="current-plan-card" className="bg-white border border-[#E5E4E0] p-8">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="label-mono">Piano attuale</span>
              <span
                className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 text-white"
                style={{ backgroundColor: statusColor }}
              >
                {statusLabel}
              </span>
            </div>
            <h2 className="font-display text-4xl font-light">{sub.plan_name}</h2>
          </div>
          {isActive && (
            <Button
              data-testid="open-portal-btn"
              onClick={openPortal}
              disabled={busy}
              className="bg-[#1C1B1A] hover:bg-[#000] text-white font-mono uppercase text-xs tracking-[0.2em]"
            >
              <CreditCard size={14} className="mr-2" />
              {busy ? "Attendere…" : "Gestisci abbonamento"}
              <ArrowUpRight size={14} className="ml-2" />
            </Button>
          )}
        </div>

        {isTrialing && sub.trial_end && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-[#FDF1EB] border border-[#C85A32]">
            <Clock size={18} className="text-[#C85A32] mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-[#1C1B1A]">Prova gratuita in corso</div>
              <div className="text-[#5C5B5A] mt-1">
                Il tuo primo addebito sarà il{" "}
                <strong>{new Date(sub.trial_end).toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                Puoi cancellare prima da &quot;Gestisci abbonamento&quot; senza costi.
              </div>
            </div>
          </div>
        )}

        {sub.status === "past_due" && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-[#FEE2E2] border border-[#991B1B]">
            <AlertTriangle size={18} className="text-[#991B1B] mt-0.5" />
            <div className="text-sm text-[#991B1B]">
              <strong>Pagamento non riuscito.</strong> Aggiorna il metodo di pagamento dal portale per riattivare il servizio.
            </div>
          </div>
        )}

        {sub.cancel_at_period_end && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-[#FFF7E6] border border-[#D4A017]">
            <AlertTriangle size={18} className="text-[#D4A017] mt-0.5" />
            <div className="text-sm text-[#5C5B5A]">
              L&apos;abbonamento verrà annullato il{" "}
              <strong>{sub.current_period_end && new Date(sub.current_period_end).toLocaleDateString("it-IT")}</strong>.
              Puoi ripristinarlo dal portale.
            </div>
          </div>
        )}

        {/* Usage bar */}
        {isActive && quota > 0 && (
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <span className="label-mono">Consumo mensile risposte AI</span>
              <span className="font-mono text-sm">
                <strong className="text-[#1C1B1A]">{used.toLocaleString("it-IT")}</strong>
                <span className="text-[#8C8B8A]"> / {quota.toLocaleString("it-IT")}</span>
              </span>
            </div>
            <div className="w-full h-2 bg-[#F0EFEA] overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: barColor }}
              />
            </div>
            {sub.current_period_end && (
              <div className="text-xs text-[#8C8B8A] mt-2 font-mono">
                Reset al termine del ciclo: {new Date(sub.current_period_end).toLocaleDateString("it-IT")}
              </div>
            )}
          </div>
        )}

        {!isActive && (
          <div className="mt-6 text-center py-8">
            <p className="text-[#5C5B5A] mb-4">Non hai ancora un abbonamento attivo</p>
            <Link to="/pricing">
              <Button
                data-testid="choose-plan-btn"
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
              >
                Scegli un piano
                <ArrowUpRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Info */}
      {isActive && (
        <div className="bg-white border border-[#E5E4E0] p-6">
          <h3 className="font-display text-2xl mb-4">Nel portale trovi</h3>
          <ul className="space-y-2 text-sm text-[#5C5B5A]">
            {["Cambia piano (upgrade / downgrade)", "Aggiorna metodo di pagamento", "Scarica fatture in PDF", "Cancella l'abbonamento"].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[#2C4C3B] mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
